import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { SnapshotOverrideViewStore } from "../../store";
import { SnapshotOverrideView } from "../view/SnapshotOverrideView";
import { SnapshotOverrideUpdateStore } from "../../store";
import { SnapshotOverrideUpdate } from "../update/SnapshotOverrideUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type SnapshotOverrideModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const SnapshotOverrideModalContent: React.FC<SnapshotOverrideModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new SnapshotOverrideUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new SnapshotOverrideViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <SnapshotOverrideView
            snapshotOverrideViewStore={
              storeInfo.store as SnapshotOverrideViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <SnapshotOverrideUpdate
            snapshotOverrideUpdateStore={
              storeInfo.store as SnapshotOverrideUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
